// Libs
import classNames from 'classnames/bind';
import { Box, Grid2, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseLabelValueField, Loading } from '~/components/common';
// Others
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { formatCurrency, formatDecimal, formatTitleCase } from '~/utils/helper/common';
import { UnitEnum, UnitValueEnum } from '~/utils/enum/common';
import { IMaterialDetail } from '~/utils/interface/material';
import { getMaterialDetail } from '~/thunks/material/materialThunk';
// Styles, images, icons
import styles from './MaterialInformation.module.scss';
import { images } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const MaterialInformation = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/material']);
  const { id: materialId } = useParams();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [materialDetail, setMaterialDetail] = useState<IMaterialDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!materialId) return;
    handleGetMaterialDetail(materialId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetMaterialDetail = (idMaterial: string) => {
    setIsLoading(true);

    dispatch(getMaterialDetail(idMaterial))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        setMaterialDetail(data);
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderUnit = () => {
    switch (materialDetail?.unit) {
      case UnitEnum.SQ_FT:
        return UnitValueEnum.SQ_FT;

      case UnitEnum.EACH:
        return UnitValueEnum.EACH;

      default:
        return EMPTY_STRING;
    }
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='materialInformationPage' className={cx('container')}>
      <Grid2 container spacing={'24px'}>
        <Grid2 size={6}>
          <BaseLabelValueField
            label={t('tab_information_name')}
            value={materialDetail?.name || EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_part_number')}
            value={materialDetail?.partNumber || EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_description')}
            value={materialDetail?.description || EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_price')}
            value={materialDetail?.price ? formatCurrency(materialDetail?.price) : DEFAULT_NUMBER_ZERO}
          />

          <BaseLabelValueField label={t('tab_information_unit')} value={renderUnit()} />

          <BaseLabelValueField
            label={t('tab_information_quantity')}
            value={materialDetail?.quantity ? formatDecimal(Number(materialDetail?.quantity)) : EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_taxable')}
            value={formatTitleCase(String(materialDetail?.isTaxable))}
          />

          {/* <BaseLabelValueField
            label={t('tab_information_price_book')}
            value={materialDetail?.priceBook?.name || EMPTY_STRING}
          /> */}

          <BaseLabelValueField
            label={t('tab_information_price_total')}
            value={materialDetail?.total ? formatCurrency(materialDetail?.total) : EMPTY_STRING}
          />
        </Grid2>

        <Grid2 container direction={'column'} size={6} spacing={0} className={cx('item')}>
          <BaseLabelValueField label={t('tab_information_image')} bordered={false} labelSize={'auto'} />

          <Stack flex={1} className={cx('itemImage')}>
            {materialDetail?.materialImage ? (
              <img src={materialDetail?.materialImage} alt={t('common_alt_img')} className={cx('image')} />
            ) : (
              <img src={images.imgDefaultInventory} alt={t('common_alt_img')} className={cx('image')} />
            )}
          </Stack>
        </Grid2>
      </Grid2>
      {isLoading && <Loading />}
    </Box>
  );
};

export default MaterialInformation;
